'use client';

import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetch from 'use-http';
import { PhoneNumberUtil, PhoneNumberType } from 'google-libphonenumber';

import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { H2, Heading } from '@/components/ui/shared/heading';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { MaterialIcon } from '@/components/ui/shared/materialIcon';
import { Label } from '@/components/ui/label';
import {
  isBlacklistedEmail,
  blacklistedEmailMessage,
} from '@/components/shared/Validator/emailBlacklist';

const phoneUtil = PhoneNumberUtil.getInstance();

const formSchema = z.object({
  phone: z
    .string()
    .min(1, { message: 'Telefono numeris yra privalomas.' })
    .refine((val) => /^\+(?:\d+)?$/.test(val), {
      message: `Netinkami simboliai. Naudokite tik '+' ženklą ir skaičius po jo.`,
    })
    .refine(
      (val) => {
        try {
          const number = phoneUtil.parseAndKeepRawInput(val);
          const isValid = phoneUtil.isValidNumber(number);
          if (!isValid) return false;
          const type = phoneUtil.getNumberType(number);
          return (
            type === PhoneNumberType.MOBILE ||
            type === PhoneNumberType.FIXED_LINE_OR_MOBILE
          );
        } catch {
          return false;
        }
      },
      {
        message:
          'Netinkamas numerio formatas arba ilgis arba nėra mobilaus tipo.',
      },
    ),

  email: z
    .string()
    .min(1, { message: 'El. pašto adresas yra privalomas.' })
    .email({ message: 'Neteisingas el. pašto adresas.' })
    .refine((val) => /^[\u0000-\u007F]+$/.test(val), {
      message: 'Naudokite tik lotyniškus simbolius el. pašto adrese.',
    })
    .refine((val) => !isBlacklistedEmail(val), {
      message: blacklistedEmailMessage,
    }),
});

export default function ContactForm() {
  const [newEmail, setNewEmail] = useState('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const lastInvalidEmailRef = useRef(null);

  const userState = useUserState();
  const sessionDispatch = useUserDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const {
    post: updateUserAccountRequest,
    response: updateUserAccountResponse,
  } = useFetch(`/UserAccount/Update`);

  const { post: sendSmsConfirmation } = useFetch(
    `/UserAccount/sendsmsconfirmation`,
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: userState.phoneNumber || '',
      email: userState.email || '',
    },
  });

  async function onSubmit(data) {
    const trimmedEmail = data.email.trim();
    const trimmedPhone = data.phone.trim();

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!isValidEmail) {
      if (lastInvalidEmailRef.current !== trimmedEmail) {
        lastInvalidEmailRef.current = trimmedEmail;
        form.setError('email', { message: t('validation.checkEmail') });
      }
      return;
    } else {
      lastInvalidEmailRef.current = null;
    }

    if (
      userState.email === trimmedEmail &&
      userState.phoneNumber === trimmedPhone
    ) {
      form.setError('email', { message: t('validation.emailMatch') });
      form.setError('phone', { message: t('validation.phoneMatch') });
      return;
    }

    let phoneNumberChanged = false;

    if (userState.phoneNumber !== trimmedPhone) {
      await sendSmsConfirmation('', { phoneNumber: trimmedPhone });
      phoneNumberChanged = true;
    }
    setLoading(true);
    const response = await updateUserAccountRequest('', {
      id: userState.id,
      email: userState.email,
      newEmail: trimmedEmail,
      firstName: userState.firstName,
      lastName: userState.lastName,
      phoneNumber: userState.phoneNumber,
    });
    setLoading(false);
    if (updateUserAccountResponse.status > 199) {
      if (response.status === 400) {
        form.setError('email', { message: t('validation.takenEmailV2') });
      } else {
        sessionDispatch({
          type: 'UPDATE',
          data: {
            id: response.id,
            email: response.email,
            phoneNumber: response.phoneNumber,
            personalCode: userState.personalCode,
            firstName: response.firstName,
            lastName: response.lastName,
            countryCode: userState.countryCode,
            phoneNumberConfirmed: response.phoneNumberConfirmed,
            newEmail: response.newEmail,
          },
        });
        if (!phoneNumberChanged) {
          setNewEmail(trimmedEmail);
          setPreviewModalVisible(true);
        } else {
          history.push('/verify/phone', {
            from: 'edit',
            phoneNumber: trimmedPhone,
          });
        }
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-y-2">
            <Heading variant={'userAccountTitle'}>Asmeninė informacija</Heading>
            <Label variant="subheading">Jūsų asmeniniai duomenys</Label>
          </div>
          <div className="hidden sm:block">
            <Button
              type="submit"
              loading={loading}
              variant={'light'}
              form="contact-form"
            >
              <MaterialIcon icon={'edit'} size={16} />
              Išsaugoti pakeitimus
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              id="contact-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefono numeris</FormLabel>
                    <FormControl>
                      <Input autoFocus placeholder="+370..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>El. pašto adresas</FormLabel>
                    <FormControl>
                      <Input placeholder="example@gmail.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="block sm:hidden pb-4">
                <Button type="submit" variant={'light'} form="contact-form">
                  <MaterialIcon icon={'edit'} size={16} />
                  Išsaugoti pakeitimus
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Dialog
        open={previewModalVisible}
        onOpenChange={() => setPreviewModalVisible(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>El. pašto keitimas inicijuotas</DialogTitle>
          </DialogHeader>
          <DialogBody>
            Keitimo patvirtinimas išsiųstas į <strong>{newEmail}</strong>
          </DialogBody>
          <DialogFooter>
            <Button
              variant={'secondary'}
              onClick={() => {
                setPreviewModalVisible(false);
              }}
            >
              Gerai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
